import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import CompetitionStatus from '../../components/common/CompetitionStatus/CompetitionStatus';
import ButtonSpinner from '../../components/common/ButtonSpinner/ButtonSpinner';
import DocxDownloadButton from '../../components/common/DocxDownloadButton/DocxDownloadButton';
import { useToast } from '../../context/ToastContext';
import {
  getMyWorkspace, saveSubmission,
  uploadSubmissionFile, getSubmissionFileUrl, requestResubmission,
} from '../../services/competitionWorkflowService';
import { getSubmissionComments, addSubmissionComment } from '../../services/compMatcherService';
import { formatDate } from '../../utils/helpers';
import styles from './StudentCompetitionApplication.module.css';

const countWords = (t) => (t && t.trim() ? t.trim().split(/\s+/).length : 0);

// A dropdown answer lives in the same `content` column as written text, so
// reviewers and .docx exports need no special case. An "Other" answer carries
// this prefix — the one place the frontend and `_validate_choice` in
// competition_workflow_service.py have to agree.
const OTHER_PREFIX = 'Other: ';
const OTHER = 'Other';

// ---- reviewer comments on one component ------------------------------------
//
// The reviewer's anchored comments were previously visible only in the admin
// review modal, so the student never saw the feedback written for them — only
// the single `feedback` blob that accompanies "changes requested".
//
// Shown here as a plain thread rather than highlighted over the text: the
// student edits that text, which is exactly what breaks the anchors. A comment
// whose quote no longer matches is reported by the API as `orphaned`, and is
// shown with its quote so it still reads as being about something.
const CommentThread = ({ submissionId }) => {
  const toast = useToast();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!submissionId) return;
    try {
      const data = await getSubmissionComments(submissionId, false);
      setComments(data.comments || []);
    } catch {
      // A failed comment load must not blank out the editor above it.
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await addSubmissionComment(submissionId, { body: reply });
      setReply('');
      await load();
      toast.success('Reply sent.');
    } catch (e) {
      toast.error(e.message || 'Could not send reply.');
    } finally {
      setSending(false);
    }
  };

  if (loading || (!comments.length && !submissionId)) return null;

  return (
    <div className={styles.comments}>
      <div className={styles.commentsHead}>
        Reviewer comments{comments.length ? ` (${comments.length})` : ''}
      </div>

      {comments.length === 0 ? (
        <p className={styles.commentsEmpty}>No comments yet.</p>
      ) : (
        comments.map((c) => (
          <div key={c.id} className={styles.comment}>
            <div className={styles.commentAuthor}>{c.author_name || 'Reviewer'}</div>
            {c.quoted_text && <blockquote className={styles.commentQuote}>“{c.quoted_text}”</blockquote>}
            {c.orphaned && (
              <p className={styles.commentStale}>
                You’ve edited this section since this was written.
              </p>
            )}
            <p className={styles.commentBody}>{c.body}</p>
          </div>
        ))
      )}

      <div className={styles.replyRow}>
        <input
          className={styles.replyInput}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Reply to your reviewer…"
          disabled={sending}
        />
        <button className={styles.replyBtn} onClick={send} disabled={sending || !reply.trim()}>
          {sending && <ButtonSpinner size={12} />}Send
        </button>
      </div>
    </div>
  );
};

// ---- asking to redo an approved component ----------------------------------
//
// Three states, all of them this component's job to tell apart:
//   * nothing asked yet            -> the button, then a reason box
//   * an ask is open               -> what was asked, and that it is waiting
//   * the last ask was denied      -> the refusal, and the button again
// An approved request needs no state of its own: the component is reopened by
// then, so the editor is back and this never renders.
const ResubmissionAsk = ({ leaf, submissionId, onAsked }) => {
  const toast = useToast();
  const request = leaf.resubmission_request;
  const pending = request?.status === 'pending';
  const denied = request?.status === 'denied';
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!reason.trim()) {
      toast.warning('Please say why you need to redo this — your mentor sees it.');
      return;
    }
    setBusy(true);
    try {
      await requestResubmission(submissionId, reason.trim());
      toast.success('Sent to your mentor. They have been messaged on WhatsApp.');
      setOpen(false);
      setReason('');
      onAsked();
    } catch (e) {
      toast.error(e.message || 'Could not send the request.');
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <div className={styles.resubPending}>
        <strong>🔄 Waiting on your mentor</strong>
        <p className={styles.resubReason}>“{request.reason}”</p>
        <span className={styles.resubHint}>
          They have been tagged on WhatsApp. You will get a message either way.
        </span>
      </div>
    );
  }

  return (
    <div className={styles.resubBlock}>
      {denied && (
        <div className={styles.resubDenied}>
          <strong>Your mentor kept this as approved.</strong>
          {request.decision_note && <p className={styles.resubReason}>“{request.decision_note}”</p>}
          <span className={styles.resubHint}>You can ask again if something has changed.</span>
        </div>
      )}
      {!open ? (
        <button type="button" className={styles.resubBtn} onClick={() => setOpen(true)}>
          Request to resubmit
        </button>
      ) : (
        <>
          <label className={styles.resubLabel} htmlFor={`resub-${submissionId}`}>
            Why do you need to redo this?
          </label>
          <textarea
            id={`resub-${submissionId}`}
            className={styles.resubInput}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. I found a mistake in my results table"
            rows={3}
            disabled={busy}
          />
          <div className={styles.resubActions}>
            <button
              type="button"
              className={styles.resubCancel}
              onClick={() => { setOpen(false); setReason(''); }}
              disabled={busy}
            >
              Cancel
            </button>
            <button type="button" className={styles.resubBtn} onClick={send} disabled={busy}>
              {busy && <ButtonSpinner size={12} />}Send to mentor
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ---- one component editor --------------------------------------------------
const LeafEditor = ({ leaf, complianceOn, onSaved }) => {
  const toast = useToast();
  const sub = leaf.submission || {};
  const [content, setContent] = useState(sub.content || '');
  const [link, setLink] = useState(sub.link_url || '');
  // A saved "Other" answer is one string ("Other: homemade sensor"); the form
  // splits it back into the selected option and its free text.
  const savedIsOther = (sub.content || '').startsWith(OTHER_PREFIX);
  const [choice, setChoice] = useState(savedIsOther ? OTHER : (sub.content || ''));
  const [otherText, setOtherText] = useState(savedIsOther ? sub.content.slice(OTHER_PREFIX.length) : '');
  const [picks, setPicks] = useState(
    () => (sub.content || '').split('\n').map((v) => v.trim()).filter(Boolean),
  );
  const [file, setFile] = useState(null);
  const [busyAction, setBusyAction] = useState(null); // 'draft' | 'submit' | 'file'
  const [copied, setCopied] = useState(false);
  const busy = busyAction !== null;

  const isText = leaf.submission_type === 'rich_text';
  const isChoice = leaf.submission_type === 'select';
  // A multi-select's ticked options live in the same `content` column as every
  // other answer, newline-separated — so nothing downstream (review panel,
  // deadline watch, .docx export) has to know it isn't ordinary text.
  const isMultiChoice = leaf.submission_type === 'multi_select';
  const takesFile = ['file', 'file_or_link'].includes(leaf.submission_type);
  const takesLink = ['link', 'file_or_link'].includes(leaf.submission_type);
  const status = sub.status || 'not_started';
  const approved = status === 'approved';
  const locked = leaf.locked;
  const hasText = !!(sub.content && sub.content.trim());
  const words = countWords(content);
  const min = leaf.min_words, max = leaf.max_words;
  const wordError = isText && ((min && words < min) || (max && words > max));
  const choiceValue = choice === OTHER ? `${OTHER_PREFIX}${otherText.trim()}` : choice;
  const choiceError = isChoice && (!choice || (choice === OTHER && !otherText.trim()));
  // Required means at least one tick. An optional multi-select (the nominator
  // declarations on a Diana nomination) may legitimately be submitted empty.
  const multiChoiceError = isMultiChoice && leaf.required !== false && picks.length === 0;
  const togglePick = (option) => setPicks(
    (current) => (current.includes(option)
      ? current.filter((v) => v !== option)
      // Rebuilt from the configured order rather than appended, so the saved
      // answer always reads in the order the options are shown.
      : (leaf.options || []).filter((o) => current.includes(o) || o === option)),
  );

  const persist = async (submit) => {
    setBusyAction(submit ? 'submit' : 'draft');
    try {
      // Upload a chosen file first so its path is attached before submit.
      if (file) {
        await uploadSubmissionFile(sub.id, file);
        setFile(null);
      }
      const payload = { submit };
      if (isText) payload.content = content;
      if (isChoice) payload.content = choiceValue;
      if (isMultiChoice) payload.content = picks.join('\n');
      if (takesLink) payload.link_url = link;
      await saveSubmission(sub.id, payload);
      toast.success(submit ? 'Submitted for review.' : 'Draft saved.');
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Could not save.');
    } finally {
      setBusyAction(null);
    }
  };

  // Copy the approved answer. A file can't go on the clipboard, so what's
  // copied is the text and/or the link — whatever is actually there.
  const copyable = [hasText ? sub.content : '', sub.link_url || ''].filter(Boolean).join('\n\n');

  const copy = async (e) => {
    // The button lives in the <summary>, so without this the click also toggles
    // the disclosure shut.
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(copyable);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Blocked clipboard (insecure context, denied permission) must say so
      // rather than show "Copied" over an empty clipboard.
      toast.error('Could not copy — select the text and copy it manually.');
    }
  };

  const openFile = async () => {
    setBusyAction('file');
    try {
      const url = await getSubmissionFileUrl(sub.id);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e.message || 'Could not open file.');
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className={`${styles.leaf} ${locked ? styles.lockedLeaf : ''}`}>
      <div className={styles.leafHead}>
        <div className={styles.leafTitleWrap}>
          <span className={styles.leafTitle}>{leaf.title}</span>
          {locked && <span className={styles.lockTag}>🔒 Unlocks when the previous step is approved</span>}
        </div>
        <CompetitionStatus status={status} />
      </div>

      {leaf.instructions && <p className={styles.instructions}>{leaf.instructions}</p>}
      {complianceOn && leaf.show_compliance && (
        <div className={styles.complianceInline}>⚠️ Don’t reveal your school name, city or state here.</div>
      )}

      {sub.internal_deadline && (
        <div className={styles.deadline}>Due by <strong>{formatDate(sub.internal_deadline)}</strong></div>
      )}

      {status === 'changes_required' && sub.feedback && (
        <div className={styles.feedbackBox}>
          <strong>Changes requested:</strong> {sub.feedback}
        </div>
      )}

      {sub.id && <CommentThread submissionId={sub.id} />}

      {!locked && !approved && (
        <>
          {isText && (
            <>
              <textarea
                className={styles.textarea}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={6}
                placeholder="Write here…"
              />
              <div className={`${styles.wordCounter} ${wordError ? styles.wordError : ''}`}>
                {words} words
                {(min || max) && (
                  <span> · limit {min ? `${min}–` : 'up to '}{max || '∞'}</span>
                )}
              </div>
            </>
          )}
          {isChoice && (
            <>
              <select
                className={styles.choiceSelect}
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
              >
                <option value="">Select one…</option>
                {(leaf.options || []).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
                {leaf.allow_other && <option value={OTHER}>{OTHER}</option>}
              </select>
              {choice === OTHER && (
                <input
                  className={styles.linkInput}
                  type="text"
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Tell us what it is…"
                />
              )}
            </>
          )}
          {isMultiChoice && (
            <div className={styles.checkboxGroup}>
              {(leaf.options || []).map((o) => (
                <label key={o} className={styles.checkboxOption}>
                  <input
                    type="checkbox"
                    checked={picks.includes(o)}
                    onChange={() => togglePick(o)}
                  />
                  <span>{o}</span>
                </label>
              ))}
            </div>
          )}
          {takesFile && (
            <div className={styles.uploadRow}>
              <input type="file" onChange={(e) => setFile(e.target.files[0] || null)} />
              {sub.file_path && (
                <button className={styles.linkBtn} onClick={openFile} disabled={busy}>
                  {busyAction === 'file' && <ButtonSpinner size={12} />}📎 {sub.file_name || 'current file'}
                </button>
              )}
            </div>
          )}
          {takesLink && (
            <input
              className={styles.linkInput}
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="Paste a link (Loom / Zoom / Drive)…"
            />
          )}

          {leaf.help_text && <p className={styles.helpText}>{leaf.help_text}</p>}

          <div className={styles.actions}>
            <button className={styles.draftBtn} disabled={busy} onClick={() => persist(false)}>
              {busyAction === 'draft' && <ButtonSpinner size={12} />}Save Draft
            </button>
            <button
              className={styles.submitBtn}
              disabled={busy || (isText && wordError) || choiceError || multiChoiceError}
              onClick={() => persist(true)}
            >
              {busyAction === 'submit' && <ButtonSpinner size={12} />}
              {status === 'submitted' ? 'Re-submit' : 'Submit for Review'}
            </button>
          </div>
        </>
      )}

      {/* Approved work is read-only, but the student still gets to read it back.
          The editor above is hidden once approved, which previously meant the
          student could no longer see what was actually approved. */}
      {approved && (hasText || sub.file_path || sub.link_url) && (
        <details className={styles.approvedView}>
          <summary className={styles.approvedViewHead}>
            <span>View your submission</span>
            {copyable && (
              <button type="button" className={styles.copyBtn} onClick={copy}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            )}
          </summary>
          {hasText && <div className={styles.approvedText}>{sub.content}</div>}
          {sub.file_path && (
            <button className={styles.linkBtn} onClick={openFile} disabled={busy}>
              {busyAction === 'file' && <ButtonSpinner size={12} />}📎 {sub.file_name || 'submitted file'}
            </button>
          )}
          {sub.link_url && (
            <a className={styles.approvedLink} href={sub.link_url} target="_blank" rel="noopener noreferrer">
              🔗 {sub.link_url}
            </a>
          )}
        </details>
      )}

      {approved && (
        <div className={styles.approvedNote}>
          ✅ Approved{sub.reviewed_at ? ` on ${formatDate(sub.reviewed_at)}` : ''}. No further action needed.
        </div>
      )}
      {/* A reviewer can leave a note when approving, not only when asking for
          changes. It was written for the student, so show it to them — the
          feedbackBox above is gated on `changes_required` and never fires here. */}
      {approved && sub.feedback && (
        <div className={styles.feedbackBox}>
          <strong>Reviewer&apos;s note:</strong> {sub.feedback}
        </div>
      )}
      {/* The way back out of an approved component. Approved work is otherwise
          final — the editor is gone and the backend refuses to save — so the
          student asks their mentor, who decides. */}
      {approved && (
        <ResubmissionAsk leaf={leaf} submissionId={sub.id} onAsked={onSaved} />
      )}
      {status === 'submitted' && !locked && (
        <div className={styles.submittedNote}>Submitted — awaiting review. You can still re-submit until it’s approved. Saving a draft withdraws it from review until you submit again.</div>
      )}
    </div>
  );
};

// ---- page ------------------------------------------------------------------
// The competition is the one named in the URL (/student/competitions/:templateId/application).
// A malformed id is caught here rather than sent on, because the API would try
// it against a uuid column and fail with a server error instead of a 404.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const StudentCompetitionApplication = () => {
  const toast = useToast();
  const { templateId } = useParams();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // `isCurrent` lets the effect below discard a response that arrives after the
  // URL has moved on to another competition.
  const loadWorkspace = useCallback(async (id, isCurrent = () => true) => {
    setLoading(true);
    try {
      const ws = await getMyWorkspace(id);
      if (!isCurrent()) return;
      setWorkspace(ws);
      setNotFound(false);
    } catch (e) {
      if (!isCurrent()) return;
      // The API answers 404 when the student is not enrolled in this competition.
      if (e.response?.status === 404) {
        setWorkspace(null);
        setNotFound(true);
      } else {
        toast.error(e.message || 'Failed to load workspace.');
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setWorkspace(null);
    setNotFound(false);
    if (!UUID_RE.test(templateId || '')) {
      setNotFound(true);
      setLoading(false);
      return undefined;
    }
    let current = true;
    loadWorkspace(templateId, () => current);
    return () => { current = false; };
  }, [templateId, loadWorkspace]);

  // Next action = nearest-deadline component that still needs work.
  const nextAction = useMemo(() => {
    if (!workspace) return null;
    const leaves = [];
    const walk = (nodes) => (nodes || []).forEach((n) => {
      if (n.submission_type === 'group') walk(n.children);
      else leaves.push(n);
    });
    walk(workspace.requirements);
    const open = leaves.filter((l) => !l.locked && (l.submission?.status || 'not_started') !== 'approved');
    open.sort((a, b) => (a.submission?.internal_deadline || '9999') > (b.submission?.internal_deadline || '9999') ? 1 : -1);
    return open[0] || null;
  }, [workspace]);

  if (loading && !workspace) return <div className={styles.loading}><LoadingSpinner /></div>;

  if (notFound) {
    return (
      <div className={styles.page}>
        <h1>Competitions</h1>
        <div className={styles.emptyCard}>
          <p>This competition could not be found, or you are not enrolled in it.</p>
          <Link
            to="/student/competitions"
            className={styles.draftBtn}
            style={{ marginTop: '1rem', textDecoration: 'none' }}
          >
            Back to my competitions
          </Link>
        </div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div>
            <h1>Competitions</h1>
          </div>
        </div>
        <div className={styles.emptyCard}>
          <p>Failed to load competition workspace.</p>
          <button
            type="button"
            className={styles.draftBtn}
            onClick={() => loadWorkspace(templateId)}
            style={{ marginTop: '1rem' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const t = workspace?.template;
  const s = workspace?.summary;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>{t?.name || 'Competitions'}</h1>
          {t?.final_deadline && <p className={styles.sub}>Final deadline: <strong>{formatDate(t.final_deadline)}</strong></p>}
        </div>
      </div>

      {t?.compliance_warning && (
        <div className={styles.complianceBanner}>⚠️ {t.compliance_warning}</div>
      )}

      {s && (
        <div className={styles.progressCard}>
          <div className={styles.progressTop}>
            <span>Your progress</span>
            <span>{s.progress_done}/{s.progress_total} complete</span>
          </div>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${s.progress_total ? (s.progress_done / s.progress_total) * 100 : 0}%` }} />
          </div>
          {nextAction && (
            <div className={styles.nextAction}>
              👉 Next: <strong>{nextAction.title}</strong>
              {nextAction.submission?.internal_deadline && <> by {formatDate(nextAction.submission.internal_deadline)}</>}
            </div>
          )}
        </div>
      )}

      {(workspace?.requirements || []).map((node) => (
        node.submission_type === 'group' ? (
          <section key={node.id} className={styles.group}>
            <h2 className={styles.groupTitle}>{node.title}</h2>
            {node.instructions && <p className={styles.groupInstructions}>{node.instructions}</p>}
            {(node.children || []).map((child) => (
              <LeafEditor key={child.id} leaf={child} complianceOn={!!t?.compliance_warning} onSaved={() => loadWorkspace(templateId)} />
            ))}
          </section>
        ) : (
          <LeafEditor key={node.id} leaf={node} complianceOn={!!t?.compliance_warning} onSaved={() => loadWorkspace(templateId)} />
        )
      ))}

      {workspace?.enrollment && (t?.documents || []).length > 0 && (
        <div className={styles.deliverables}>
          <span className={styles.deliverLabel}>Download Consolidated Application (.docx)</span>
          <div className={styles.deliverBtns}>
            {t.documents.map((d) => (
              <DocxDownloadButton
                key={d.doc_key}
                enrollmentId={workspace.enrollment.id} docKey={d.doc_key}
                label={d.title} approved={s?.statuses?.[d.doc_key] === 'approved'}
                prominent
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentCompetitionApplication;
